import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authFetch } from '../../Utilities/Api';
import AuthContext from '../../context/AuthContext';
import { useChatContext } from '../../context/ChatContext';
import { getSocket } from '../../socket';
import './FriendsList.css';

const FriendsList = () => {
    const [friendsData, setFriendsData] = useState([]); // Stores the full wrapper objects
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const { user } = useContext(AuthContext);
    const { openChatWithFriend } = useChatContext();
    const navigate = useNavigate();

    // ------------------------------------------------
    // 1. DATA LOADING
    // ------------------------------------------------
    useEffect(() => {
        if (!user?._id) {
            setLoading(false);
            return;
        }

        const socket = getSocket();
        if (socket && !socket.connected) socket.connect();

        const loadFriends = async () => {
            try {
                // Returns array of: { user: {...}, relation: '...', lastMessage: ... }
                const res = await authFetch('/friends');
                const data = await res.json();

                if (Array.isArray(data)) {
                    setFriendsData(data);
                } else {
                    setFriendsData([]);
                }
            } catch (err) {
                console.error('Failed to fetch friends:', err);
                setFriendsData([]);
            } finally {
                setLoading(false);
            }
        };

        const handleFriendListUpdate = () => setTimeout(loadFriends, 500);

        loadFriends();

        if (socket) socket.on('friendListUpdated', handleFriendListUpdate);
        return () => {
            if (socket) socket.off('friendListUpdated', handleFriendListUpdate);
        };
    }, [user]);

    // ------------------------------------------------
    // 2. HELPERS
    // ------------------------------------------------
    const formatLastSeen = (dateString) => {
        if (!dateString) return "Last seen: Unknown";
        const date = new Date(dateString);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    };

    // ✅ NEW: Actually save the tag to the database
    const handleRelationshipChange = async (friendId, newStatus) => {
        // 1. Optimistic Update (Change UI immediately)
        setFriendsData(prev => prev.map(item => {
            if (item.user._id === friendId) {
                return { ...item, relation: newStatus };
            }
            return item;
        }));

        // 2. Send to Backend
        try {
            const res = await authFetch(`/friends/update-relation/${friendId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ relation: newStatus })
            });

            if (res.ok) {
                toast.success(`Updated to ${newStatus}`);
            } else {
                throw new Error("Failed");
            }
        } catch (err) {
            toast.error("Failed to update relationship");
            // Revert changes could go here if needed
        }
    };

    // ------------------------------------------------
    // 3. ACTIONS
    // ------------------------------------------------
    const handleMessage = async (friendUser) => {
        try {
            await openChatWithFriend(friendUser._id);
            toast.info(`Opening chat with ${friendUser.nickname || friendUser.username}...`);
        } catch (err) {
            toast.error("Could not open chat.");
        }
    };

    const handleUnfriend = async (friendId, name) => {
        if (!window.confirm(`Unfriend ${name}?`)) return;
        try {
            const res = await authFetch(`/friends/remove/${friendId}`, { method: 'DELETE' });
            if (res.ok) toast.success(`Removed ${name}.`);
        } catch (err) {
            toast.error('Network error.');
        }
    };

    const handleBlock = async (friendId, name) => {
        if (!window.confirm(`Block ${name}?`)) return;
        try {
            const res = await authFetch(`/friends/block/${friendId}`, { method: 'PUT' });
            if (res.ok) toast.success(`Blocked ${name}.`);
        } catch (err) {
            toast.error('Network error.');
        }
    };

    // ------------------------------------------------
    // 4. RENDER
    // ------------------------------------------------

    // Filter logic updated to access item.user
    const filteredItems = friendsData.filter(item => {
        const friendUser = item.user;
        if (!friendUser) return false;

        if (!searchTerm.trim()) return true;

        const term = searchTerm.toLowerCase();
        return (friendUser.username?.toLowerCase() || '').includes(term) ||
            (friendUser.nickname?.toLowerCase() || '').includes(term);
    });

    if (loading) return <div className="friends-wrapper"><div className="spinner"></div></div>;

    return (
        <div className="friends-wrapper">
            <h2 className="friends-title">Your Friends</h2>

            <div className="friends-search">
                <input
                    type="text"
                    className="friends-search-input"
                    placeholder="Search friends..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <ul className="friends-list">
                {filteredItems.length === 0 ? (
                    <p className="no-friends">No friends found.</p>
                ) : (
                    filteredItems.map(item => {
                        const friend = item.user;
                        const relation = item.relation || 'Friend'; // Get tag from DB

                        const displayName = friend.nickname || friend.username;
                        const isOnline = friend.isOnline;

                        return (
                            <li key={friend._id} className="friend-card">

                                {/* --- TOP ROW: Name & Dropdown --- */}
                                <div className="card-top-row">
                                    <span className="friend-name">{displayName}</span>

                                    {/* ✅ Connected Dropdown */}
                                    <select
                                        className="relationship-select"
                                        value={relation}
                                        onChange={(e) => handleRelationshipChange(friend._id, e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <option value="Acquaintance">Acquaintance</option>
                                        <option value="Friend">Friend</option>
                                        <option value="Close Friend">Close Friend</option>
                                        <option value="Best Friend">Best Friend</option>
                                        <option value="Family">Family</option>
                                    </select>
                                </div>

                                {/* --- BOTTOM ROW: Avatar/Status & Buttons --- */}
                                <div className="card-bottom-row">

                                    {/* Left: Avatar & Last Seen */}
                                    <div className="avatar-section" onClick={() => navigate(`/profile/${friend.username}`)}>
                                        <div className="avatar-container-large">
                                            <img
                                                src={friend.profilePicture || "/default-avatar.png"}
                                                alt={displayName}
                                                className="friend-avatar-large"
                                            />
                                            <span className={`status-dot-large ${isOnline ? 'online' : 'offline'}`}></span>
                                        </div>
                                        <span className="last-seen-text">
                                            {isOnline ? "Online" : formatLastSeen(friend.lastActive)}
                                        </span>
                                    </div>

                                    {/* Right: Text Buttons */}
                                    <div className="action-buttons-row">
                                        <button
                                            className="btn-word"
                                            onClick={(e) => { e.stopPropagation(); handleMessage(friend); }}
                                        >
                                            Message
                                        </button>
                                        <button
                                            className="btn-word"
                                            onClick={(e) => { e.stopPropagation(); handleUnfriend(friend._id, displayName); }}
                                        >
                                            Unfriend
                                        </button>
                                        <button
                                            className="btn-word"
                                            onClick={(e) => { e.stopPropagation(); handleBlock(friend._id, displayName); }}
                                        >
                                            Block
                                        </button>
                                    </div>
                                </div>
                            </li>
                        );
                    })
                )}
            </ul>
        </div>
    );
};

export default FriendsList;